import { createId } from "@paralleldrive/cuid2";
import {
  blob,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { z } from "zod";
import { createdAt, deletedAt, updatedAt } from "../lib/types";
import { cuid, dateTime, string } from "../validator/common";

export enum ProductType {
  TSHIRT = 0,
  WEIGHTS = 1,
  EQUIPMENT = 2,
  ACCESSORIES = 3,
  OTHER = 4,
}

export const productTypeNames = [
  "T-Shirt",
  "Ağırlık",
  "Ekipman",
  "Aksesuar",
  "Diğer",
] as const;

export function getProductTypeName(type: ProductType): string {
  return productTypeNames[type];
}

export type ProductTypeName = (typeof productTypeNames)[number];

// Product data structure stored in blob
export const productDataSchema = z.object({
  name: string.min(3, "Ürün adı en az 3 karakter olmalı"),
  description: string.min(10, "Açıklama en az 10 karakter olmalı"),
  price: z.number().positive("Fiyat pozitif olmalı"),
  oldPrice: z.number().positive("Eski fiyat pozitif olmalı").optional(),
  imageUrl: z.url("Geçerli bir URL olmalı").optional(),
  sizes: z.array(z.string()).optional(),
  colors: z.array(z.string()).optional(),
  stock: z.number().int().min(0).optional(),
  isFeatured: z.boolean().default(false),
});

export type ProductData = z.infer<typeof productDataSchema>;

export const products = sqliteTable(
  "products",
  {
    productId: text("product_id")
      .primaryKey()
      .notNull()
      .$defaultFn(() => createId()),
    productType: integer("product_type")
      .notNull()
      .default(ProductType.OTHER)
      .$type<ProductType>(),
    data: blob("data", { mode: "json" }).$type<ProductData>().notNull(),
    isActive: integer("is_active", { mode: "boolean" }).default(true).notNull(),
    displayOrder: integer("display_order").notNull().default(0),
    createdAt,
    updatedAt,
    deletedAt,
  },
  (t) => [uniqueIndex("unique_product_id").on(t.productId)],
);

export const selectProductSchema = z.object({
  productId: cuid,
  productType: z.enum(ProductType),
  data: productDataSchema,
  isActive: z.boolean(),
  displayOrder: z.number().int(),
  createdAt: dateTime,
  updatedAt: dateTime.nullable(),
  deletedAt: dateTime.nullable(),
});

export const insertProductSchema = z.object({
  productType: z.enum(ProductType),
  data: productDataSchema,
  displayOrder: z.number().int().min(0).optional(),
});

export const patchProductSchema = z.object({
  productId: cuid,
  productType: z.enum(ProductType).optional(),
  data: productDataSchema.partial().optional(),
  isActive: z.boolean().optional(),
  displayOrder: z.number().int().min(0).optional(),
});

export type SelectProduct = z.infer<typeof selectProductSchema>;
export type InsertProduct = z.infer<typeof insertProductSchema>;
export type PatchProduct = z.infer<typeof patchProductSchema>;
